import { artifactLink, bindArtifactLightboxes } from "./artifact-links.js";

export function isStructureDiscussionTicket(ticket) {
  return ticket?.spec?.mode === "building_conversation_reply_v1" && Boolean(ticket.spec.conversationId);
}

export function structureDiscussionProgress(ticket) {
  const status = String(ticket?.status || "unknown").toLowerCase();
  return {
    status,
    delivered: status === "submitted",
    failed: ["failed", "escalated", "cancelled", "canceled"].includes(status),
  };
}

export function initTickets({
  api,
  toast,
  esc,
  fileUrl = (key) => `/files/${key}`,
  hrefFor = null,
  onNavigate = null,
  onRouteStateChange = null,
}) {
  const root = document.getElementById("panel");
  let selectedTicketId = localStorage.getItem("wl_selected_ticket") || "";
  let tickets = [];
  let detail = null;
  let runs = [];
  let artifacts = [];
  let relatedTickets = [];
  let loadingDetail = false;
  let filter = localStorage.getItem("wl_ticket_filter") || "active";

  const FILTERS = {
    active: ["ready", "blocked", "leased", "in_progress", "submitted", "review", "failed", "escalated"],
    waiting: ["ready", "blocked"],
    running: ["leased", "in_progress"],
    review: ["submitted", "review"],
    done: ["approved", "rejected", "cancelled"],
    all: [],
  };

  async function render() {
    root.innerHTML = `<div class="ticket-page"><div class="ticket-shell muted">Loading tickets...</div></div>`;
    try {
      const projectId = document.body.dataset.projectId || "";
      if (!projectId) throw new Error("Select a project first");
      const res = await api(`/tickets?projectId=${encodeURIComponent(projectId)}`);
      tickets = Array.isArray(res.tickets) ? res.tickets : [];
      if (!selectedTicketId) selectedTicketId = tickets[0]?.id || "";
      await loadSelected({ renderAfter: false });
      renderShell();
    } catch (err) {
      root.innerHTML = `<div class="panel-inner"><h2>Tickets</h2><p class="muted">${esc(String(err))}</p></div>`;
    }
  }

  async function open(id, { publish = false } = {}) {
    selectedTicketId = id || selectedTicketId;
    localStorage.setItem("wl_selected_ticket", selectedTicketId);
    if (publish) publishRoute(ticketRoute(selectedTicketId));
    await render();
  }

  function renderShell() {
    const filtered = filteredTickets();
    const selected = tickets.find((ticket) => ticket.id === selectedTicketId);
    const selectedOutsideFilter = selected && !filtered.some((ticket) => ticket.id === selected.id);
    const visible = selectedOutsideFilter ? [selected, ...filtered] : filtered;
    root.innerHTML = `<div class="ticket-page">
      <div class="ticket-head">
        <div>
          <h2>Ticket detail</h2>
          <p class="muted">Advanced execution history. Return to the Board for the production overview.</p>
        </div>
        <div class="map-actions">
          <input id="ticket-search" placeholder="paste ticket id" value="${esc(selectedTicketId)}" />
          <button type="button" id="ticket-open-id">Open</button>
          <button type="button" id="ticket-refresh">Refresh</button>
        </div>
      </div>
      <div class="ticket-filter-row">
        ${Object.keys(FILTERS).map((key) => `<button type="button" data-ticket-filter="${key}" class="${filter === key ? "on" : ""}">${esc(key)}</button>`).join("")}
      </div>
      <div class="ticket-layout">
        <aside class="ticket-list">
          ${selectedOutsideFilter ? `<p class="ticket-filter-notice">Opened ticket is outside the “${esc(filter)}” filter.</p>` : ""}
          ${visible.length ? visible.map(ticketListItem).join("") : `<p class="muted empty-pad">No ${esc(filter)} tickets.</p>`}
        </aside>
        <section class="ticket-detail">
          ${loadingDetail ? `<div class="ticket-card muted">Loading ticket detail...</div>` : renderTicketDetail()}
        </section>
      </div>
    </div>`;
    bind();
  }

  function filteredTickets() {
    const statuses = FILTERS[filter] || [];
    const list = statuses.length ? tickets.filter((ticket) => statuses.includes(ticket.status)) : tickets;
    return [...list].sort((a, b) => String(b.updatedAt || b.createdAt || "").localeCompare(String(a.updatedAt || a.createdAt || "")));
  }

  function ticketListItem(ticket) {
    const on = ticket.id === selectedTicketId;
    return `<a class="ticket-list-item ${on ? "on" : ""}" data-ticket-id="${esc(ticket.id)}" href="${esc(routeHref(ticketRoute(ticket.id)))}"${on ? " aria-current=\"page\"" : ""}>
      <span class="badge ${esc(ticket.status)}">${esc(ticket.status)}</span>
      <b>${esc(ticket.title || ticket.type || ticket.id)}</b>
      <small>${esc(ticket.type || "ticket")} · ${esc(ticket.id.slice(0, 8))}${ticket.worldId ? ` · world ${esc(ticket.worldId.slice(0, 8))}` : ""}</small>
      <small>${esc(relativeTime(ticket.updatedAt || ticket.createdAt))} · attempt ${esc(ticket.attempt ?? 0)}/${esc(ticket.maxAttempts ?? 0)}</small>
    </a>`;
  }

  function renderTicketDetail() {
    const ticket = detail?.ticket || tickets.find((item) => item.id === selectedTicketId);
    if (!ticket) {
      return `<div class="ticket-card muted">Select a ticket or paste a ticket id.</div>`;
    }
    const tracker = ticketTracker(ticket);
    const conversationReply = isStructureDiscussionTicket(ticket);
    return `<div class="ticket-card ticket-primary ${esc(tracker.tone)}">
      <div class="ticket-title-row">
        <div>
          <h3>${esc(ticket.title || ticket.type || "Ticket")}</h3>
          <p class="muted">${esc(ticket.id)}${ticket.worldId ? ` · world ${esc(ticket.worldId)}` : ""}</p>
        </div>
        <span class="badge ${esc(ticket.status)}">${esc(ticket.status)}</span>
      </div>
      <div class="ticket-summary-grid">
        ${summaryCell("Now", tracker.now, tracker.nowHint)}
        ${summaryCell("Next", tracker.next, tracker.nextHint)}
        ${summaryCell("Expected", tracker.expected, tracker.expectedHint)}
        ${summaryCell("Worker", tracker.worker, tracker.workerHint)}
      </div>
      ${renderTicketTimeline(ticket)}
      ${conversationReply ? `<div class="ticket-conversation-return"><a href="${esc(routeHref(conversationRoute(ticket)))}">Return to this Structure discussion →</a><small>The memo is the output; it does not require artifact approval.</small></div>` : ""}
    </div>
    <div class="ticket-grid-2">
      <section class="ticket-card">
        <h3>Events</h3>
        ${renderEvents(detail?.events || [])}
      </section>
      <section class="ticket-card">
        <h3>Runs</h3>
        ${renderRuns()}
      </section>
    </div>
    <div class="ticket-grid-2">
      <section class="ticket-card">
        <h3>Artifacts</h3>
        ${renderArtifacts()}
      </section>
      <section class="ticket-card">
        <h3>Related Tickets</h3>
        ${renderRelated(ticket)}
      </section>
    </div>
    ${renderTicketActions(ticket)}
    <section class="ticket-card">
      <h3>Spec</h3>
      <pre class="json">${esc(JSON.stringify(ticket.spec || {}, null, 2))}</pre>
    </section>
    ${ticket.result ? `<section class="ticket-card"><h3>Result</h3><pre class="json">${esc(JSON.stringify(ticket.result, null, 2))}</pre></section>` : ""}`;
  }

  function summaryCell(label, value, hint) {
    return `<div>
      <small>${esc(label)}</small>
      <strong>${esc(value)}</strong>
      <span>${esc(hint || "")}</span>
    </div>`;
  }

  function ticketTracker(ticket) {
    const activeRun = runs.find((run) => !run.finishedAt && !["succeeded", "failed", "cancelled"].includes(String(run.status || "")));
    const latestArtifact = artifacts.find((artifact) => String(artifact.kind || "").toLowerCase() === "gltf" || String(artifact.uri || "").toLowerCase().endsWith(".glb"));
    const status = ticket.status || "unknown";
    if (isStructureDiscussionTicket(ticket)) return conversationTicketTracker(ticket, activeRun);
    const relatedBlockout = relatedTickets.find((item) => item.type === "blockout" && item.id !== ticket.id);
    const now = latestArtifact ? "Artifact available" : statusToNow(status, activeRun, relatedBlockout);
    const next = latestArtifact ? "Review output" : statusToNext(status, relatedBlockout);
    return {
      tone: latestArtifact || status === "approved" ? "ready" : ["failed", "rejected", "cancelled", "escalated"].includes(status) ? "blocked" : "active",
      now,
      nowHint: latestArtifact ? latestArtifact.uri : `Status ${status}${ticket.worldId ? ` · world ${ticket.worldId.slice(0, 8)}` : ""}`,
      next,
      nextHint: nextHint(status, ticket, relatedBlockout),
      expected: statusToEta(status, latestArtifact, relatedBlockout),
      expectedHint: ticket.createdAt ? `Filed ${relativeTime(ticket.createdAt)}` : "",
      worker: activeRun ? `${activeRun.agentRole || "worker"} run` : runs.length ? `${runs[0].status || "run"} run` : "No run yet",
      workerHint: activeRun ? `Started ${relativeTime(activeRun.startedAt)}` : "A run appears after a worker starts.",
    };
  }

  function conversationRoute(ticket) {
    return {
      route: "structureSession",
      conversationId: ticket.spec.conversationId,
      conversationName: String(ticket.title || "Structure discussion").replace(/^Building session:\s*/i, ""),
      state: { tab: "session", pane: "conversation" },
    };
  }

  function conversationTicketTracker(ticket, activeRun) {
    const { status, delivered, failed } = structureDiscussionProgress(ticket);
    return {
      tone: failed ? "blocked" : delivered ? "ready" : "active",
      now: activeRun ? "Structure agent is replying" : delivered ? "Reply delivered" : failed ? "Reply failed" : statusToNow(status, activeRun),
      nowHint: delivered ? "The memo is stored in the working session." : `Status ${status}`,
      next: delivered ? "Continue discussion" : failed ? "Retry from session" : statusToNext(status),
      nextHint: delivered || failed ? "Return to the exact Structure discussion below." : nextHint(status, ticket),
      expected: delivered ? "Ready now" : failed ? "Stopped" : statusToEta(status),
      expectedHint: ticket.createdAt ? `Filed ${relativeTime(ticket.createdAt)}` : "",
      worker: activeRun ? `${activeRun.agentRole || "worker"} run` : runs.length ? `${runs[0].status || "run"} run` : "No run yet",
      workerHint: activeRun ? `Started ${relativeTime(activeRun.startedAt)}` : delivered ? "The agent run finished and persisted the memo." : "A run appears after a worker starts.",
    };
  }

  function statusToNow(status, activeRun, relatedBlockout) {
    if (activeRun) return "Worker is running";
    if (status === "ready") return "Queued";
    if (status === "blocked") return "Blocked";
    if (status === "leased") return "Claimed by worker";
    if (status === "in_progress") return "In progress";
    if (status === "submitted") return "Submitted";
    if (status === "review") return "Needs review";
    if (status === "approved" && relatedBlockout) return "Approved; downstream ticket exists";
    if (status === "approved") return "Approved";
    if (status === "failed") return "Failed";
    if (status === "escalated") return "Escalated";
    return status;
  }

  function statusToNext(status, relatedBlockout) {
    if (status === "ready") return "Worker pickup";
    if (status === "blocked") return "Dependency approval";
    if (status === "leased") return "Worker start";
    if (status === "in_progress") return "Worker output";
    if (status === "submitted") return "Send to review or QA";
    if (status === "review") return "Human decision";
    if (status === "approved" && relatedBlockout && relatedBlockout.status !== "approved") return `${relatedBlockout.type} ticket`;
    if (status === "approved") return "Done";
    if (status === "failed" || status === "escalated") return "Re-queue or inspect error";
    if (status === "rejected") return "Revise";
    return "Refresh";
  }

  function nextHint(status, ticket, relatedBlockout) {
    if (relatedBlockout && ticket.status === "approved") return `Related ${relatedBlockout.type} ${relatedBlockout.id.slice(0, 8)} is ${relatedBlockout.status}.`;
    if (status === "ready") return "The ticket is waiting in the queue.";
    if (status === "review") return "Approve or reject with a note.";
    if (status === "blocked") return "A dependency must complete before this can start.";
    return "Refresh for the latest event and run state.";
  }

  function statusToEta(status, artifact, relatedBlockout) {
    if (artifact) return "Ready now";
    if (status === "ready") return "Usually starts within 1-2 min";
    if (status === "leased") return "Usually starts within 1 min";
    if (status === "in_progress") return "Usually a few minutes";
    if (status === "blocked") return "Waiting on dependency";
    if (status === "review" || status === "submitted") return "Waiting on review";
    if (status === "approved" && relatedBlockout && relatedBlockout.status !== "approved") return "Downstream work pending";
    if (["failed", "rejected", "cancelled", "escalated"].includes(status)) return "Stopped";
    return "Unknown";
  }

  function renderTicketTimeline(ticket) {
    if (isStructureDiscussionTicket(ticket)) {
      const { failed, delivered } = structureDiscussionProgress(ticket);
      const steps = [
        { label: "Request sent", state: "done" },
        { label: "Agent reply", state: failed ? "failed" : delivered ? "done" : runs.length ? "active" : "pending" },
        { label: "Returned to session", state: delivered ? "done" : "pending" },
      ];
      return `<div class="ticket-timeline ticket-timeline-conversation">${steps.map((step) => `<div class="${esc(step.state)}"><span></span><b>${esc(step.label)}</b></div>`).join("")}</div>`;
    }
    const steps = [
      { label: "Filed", state: "done" },
      { label: "Worker picked up", state: runs.length || ["leased", "in_progress", "submitted", "review", "approved"].includes(ticket.status) ? "done" : "pending" },
      { label: "Output", state: ["submitted", "review", "approved"].includes(ticket.status) ? "done" : ticket.status === "in_progress" ? "active" : "pending" },
      { label: "Review", state: ticket.status === "review" ? "active" : ["approved", "rejected"].includes(ticket.status) ? "done" : "pending" },
      { label: "Artifact", state: artifacts.length ? "done" : "pending" },
    ];
    if (["failed", "escalated"].includes(ticket.status)) steps[Math.max(1, steps.findIndex((step) => step.state !== "done"))].state = "failed";
    return `<div class="ticket-timeline">${steps.map((step) => `<div class="${esc(step.state)}"><span></span><b>${esc(step.label)}</b></div>`).join("")}</div>`;
  }

  function renderEvents(events) {
    if (!events.length) return `<p class="muted">No events yet.</p>`;
    return `<ul class="ticket-events">${events.map((event) => `<li>
      <span>${esc(when(event.at))}</span>
      <b>${esc(event.fromStatus ? `${event.fromStatus} -> ${event.toStatus}` : event.toStatus || "event")}</b>
      <small>${esc(event.actor || "")}${event.actorId ? ` · ${esc(event.actorId)}` : ""}${event.note ? ` · ${esc(event.note)}` : ""}</small>
    </li>`).join("")}</ul>`;
  }

  function renderRuns() {
    if (!runs.length) return `<p class="muted">No worker run has started yet.</p>`;
    return `<div class="ticket-mini-list">${runs.map((run) => `<div>
      <b>${esc(run.agentRole || "worker")} · ${esc(run.status || "started")}</b>
      <span>${esc(run.id || "")}</span>
      <small>${esc(run.model || "none")} · ${esc(run.startedAt ? `started ${when(run.startedAt)}` : "")}${run.finishedAt ? ` · finished ${esc(when(run.finishedAt))}` : ""}${run.costUsd ? ` · $${Number(run.costUsd).toFixed(2)}` : ""}</small>
      ${run.contextSlice?.modelRouting ? `<small>${esc(run.contextSlice.modelRouting.tier)} · ${esc(run.contextSlice.modelRouting.policyVersion)} · ${esc(run.contextSlice.modelRouting.reason)}</small>` : ""}
      ${run.outputs ? `<details><summary>outputs</summary><pre class="json">${esc(JSON.stringify(run.outputs, null, 2))}</pre></details>` : ""}
    </div>`).join("")}</div>`;
  }

  function renderArtifacts() {
    if (!artifacts.length) return `<p class="muted">No artifacts have been reported yet.</p>`;
    return `<div class="ticket-mini-list">${artifacts.map((artifact) => `<div>
      <b>${esc(artifact.kind || "artifact")}</b>
      ${artifactLink({ uri: artifact.uri, label: artifact.uri, esc, fileUrl })}
      <small>${esc(artifact.createdAt ? when(artifact.createdAt) : "")}${artifact.runId ? ` · run ${esc(artifact.runId.slice(0, 8))}` : ""}</small>
    </div>`).join("")}</div>`;
  }

  function renderRelated(ticket) {
    const rows = relatedTickets.filter((item) => item.id !== ticket.id).slice(0, 8);
    if (!ticket.worldId) return `<p class="muted">This ticket is not attached to a world.</p>`;
    if (!rows.length) return `<p class="muted">No related world tickets found yet.</p>`;
    return `<div class="ticket-mini-list">${rows.map((item) => `<a data-related-ticket="${esc(item.id)}" href="${esc(routeHref(ticketRoute(item.id)))}">
      <b>${esc(item.type)} · ${esc(item.status)}</b>
      <span>${esc(item.title || item.id)}</span>
      <small>${esc(item.id.slice(0, 8))} · ${esc(relativeTime(item.updatedAt || item.createdAt))}</small>
    </a>`).join("")}</div>`;
  }

  function renderTicketActions(ticket) {
    if (isStructureDiscussionTicket(ticket)) return "";
    const canReview = ticket.status === "review";
    const canRequeue = ticket.status === "escalated";
    const canRetry = ticket.status === "failed" && ticket.spec?.mode === "building_plan_patch_v1";
    if (!canReview && !canRequeue && !canRetry && ticket.status !== "submitted") return "";
    return `<section class="ticket-card ticket-actions-card">
      <h3>Actions</h3>
      ${ticket.status === "submitted" ? `<p class="muted">The output is submitted. Its artifact revision will appear in Needs you after validation; this detail screen cannot bypass that gate.</p>` : ""}
      ${canReview ? `<p class="muted">This ticket is in review. Approving it unblocks any dependent tickets.</p>` : ""}
      <input id="ticket-note" placeholder="decision note" />
      <div class="actions">
        ${canReview ? `<button class="approve" data-ticket-act="approved">Approve</button><button class="reject" data-ticket-act="rejected">Reject</button>` : ""}
        ${canRequeue ? `<button data-ticket-act="ready">Re-queue</button>` : ""}
        ${canRetry ? `<button class="primary" data-ticket-act="retry">Retry same request</button>` : ""}
      </div>
    </section>`;
  }

  async function loadSelected({ renderAfter = true } = {}) {
    detail = null;
    runs = [];
    artifacts = [];
    relatedTickets = [];
    if (!selectedTicketId) return;
    loadingDetail = true;
    if (renderAfter) renderShell();
    try {
      detail = await api(`/tickets/${selectedTicketId}`);
      const ticket = detail.ticket || {};
      const projectId = document.body.dataset.projectId || ticket.projectId || "";
      const [runsRes, artifactsRes, relatedRes] = await Promise.all([
        api(`/runs?ticketId=${selectedTicketId}`).catch(() => ({ runs: [] })),
        api(`/artifacts?projectId=${encodeURIComponent(projectId)}&ticketId=${selectedTicketId}`).catch(() => ({ artifacts: [] })),
        ticket.worldId ? api(`/tickets?projectId=${encodeURIComponent(projectId)}&worldId=${ticket.worldId}`).catch(() => ({ tickets: [] })) : Promise.resolve({ tickets: [] }),
      ]);
      runs = Array.isArray(runsRes.runs) ? runsRes.runs : [];
      artifacts = Array.isArray(artifactsRes.artifacts) ? artifactsRes.artifacts : [];
      relatedTickets = Array.isArray(relatedRes.tickets) ? relatedRes.tickets : [];
      localStorage.setItem("wl_selected_ticket", selectedTicketId);
    } catch (err) {
      toast(String(err), true);
    } finally {
      loadingDetail = false;
      if (renderAfter) renderShell();
    }
  }

  function bind() {
    bindArtifactLightboxes(root);
    root.querySelector("#ticket-refresh")?.addEventListener("click", () => render());
    root.querySelector("#ticket-open-id")?.addEventListener("click", () => {
      const id = root.querySelector("#ticket-search")?.value.trim();
      if (!id) return;
      const descriptor = ticketRoute(id);
      if (onNavigate) onNavigate(descriptor);
      else open(id, { publish: true });
    });
    root.querySelectorAll("[data-ticket-filter]").forEach((button) => {
      button.onclick = () => {
        filter = button.dataset.ticketFilter;
        localStorage.setItem("wl_ticket_filter", filter);
        renderShell();
        publishRoute(ticketRoute(selectedTicketId));
      };
    });
    root.querySelectorAll("[data-ticket-id]").forEach((link) => {
      link.onclick = (event) => activateRouteLink(event, ticketRoute(link.dataset.ticketId), () => open(link.dataset.ticketId, { publish: true }));
    });
    root.querySelectorAll("[data-related-ticket]").forEach((link) => {
      link.onclick = (event) => activateRouteLink(event, ticketRoute(link.dataset.relatedTicket), () => open(link.dataset.relatedTicket, { publish: true }));
    });
    root.querySelectorAll("[data-ticket-act]").forEach((button) => {
      button.onclick = async () => {
        const act = button.dataset.ticketAct;
        const note = root.querySelector("#ticket-note")?.value.trim() || "";
        if (act === "rejected" && !note) {
          toast("rejection requires a feedback note", true);
          return;
        }
        try {
          if (act === "retry") {
            await api(`/tickets/${selectedTicketId}/retry`, { method: "POST", body: "{}" });
            toast("same Structure request queued for another attempt");
          } else {
            await api(`/tickets/${selectedTicketId}/transition`, { method: "POST", body: JSON.stringify({ to: act, note: note || "via ticket detail" }) });
            toast(`ticket -> ${act}`);
          }
          await render();
        } catch (err) {
          toast(String(err), true);
        }
      };
    });
  }

  function when(value) {
    return value ? new Date(value).toLocaleString() : "";
  }

  function relativeTime(value) {
    if (!value) return "unknown";
    const ms = Date.now() - new Date(value).getTime();
    if (!Number.isFinite(ms)) return when(value);
    const min = Math.max(0, Math.floor(ms / 60000));
    if (min < 1) return "just now";
    if (min < 60) return `${min}m ago`;
    const hours = Math.floor(min / 60);
    if (hours < 24) return `${hours}h ${min % 60}m ago`;
    return `${Math.floor(hours / 24)}d ago`;
  }

  async function applyRouteState({ filter: nextFilter, ticketId = "" } = {}) {
    if (Object.hasOwn(FILTERS, nextFilter)) {
      filter = nextFilter;
      localStorage.setItem("wl_ticket_filter", filter);
    }
    if (ticketId) selectedTicketId = ticketId;
    await render();
  }

  function getRouteState() {
    return {
      ...(filter === "active" ? {} : { filter }),
      ...(selectedTicketId ? { ticketId: selectedTicketId } : {}),
    };
  }

  function ticketRoute(ticketId = selectedTicketId) {
    const state = filter === "active" ? {} : { filter };
    const ticket = tickets.find((item) => item.id === ticketId)
      || relatedTickets.find((item) => item.id === ticketId)
      || (detail?.ticket?.id === ticketId ? detail.ticket : null);
    return ticketId ? { route: "ticket", ticketId, ticketName: ticket?.title || ticket?.type || "ticket", state } : { route: "tickets", state };
  }

  function routeHref(descriptor) {
    return hrefFor?.(descriptor) || "#";
  }

  function publishRoute(descriptor) {
    onRouteStateChange?.(descriptor);
  }

  function activateRouteLink(event, descriptor, fallback) {
    if (!isUnmodifiedPrimaryLinkClick(event)) return;
    event.preventDefault();
    if (onNavigate) onNavigate(descriptor);
    else fallback?.();
  }

  return { render, open, applyRouteState, getRouteState, getRouteDescriptor: () => ticketRoute() };
}

function isUnmodifiedPrimaryLinkClick(event) {
  return event.button === 0
    && !event.defaultPrevented
    && !event.metaKey
    && !event.ctrlKey
    && !event.shiftKey
    && !event.altKey;
}
